import { GUIDType } from '../interfaces/types';
import dateUtils, { UiFormats, UtcString, LocalMoment } from './utils.date';
import contextUtils, { IFarmContext } from './utils.context';
import { AnelmaEnvironment } from '../interfaces/enums';
import moment from 'moment';
import { IFarmDetails } from '../interfaces/IBusinessEntities';
import { isNotNil } from 'ramda';

class Utils {
    private readonly storageKeyPrefix = 'anelma3-';
    private readonly uniqueIds: string[] = [];

    get context() {
        return contextUtils;
    }

    get date() {
        return dateUtils;
    }

    addUniqueIdForArrayItems(array: any[]): void {
        array.forEach((i) => (i._i = this.generateUniqueId()));
    }

    calculateAgeInMonths = (
        startDate: LocalMoment | string,
        birthDate: LocalMoment | string,
        valueToUseInDivision: number
    ): number => {
        const formats = [
            'YYYY-MM-DD',
            'DD/MM/YYYY',
            'DD/MM/YY',
            'D.M.YYYY',
            'DD.MM.YYYY',
            'D.M.YYYY HH.mm.ss',
            'DD.MM.YYYY HH.mm.ss',
            'D.M.YYYY H.mm.ss',
            'DD.MM.YYYY H.mm.ss',
        ];

        let startMoment =
            typeof startDate === 'string' ? moment(startDate, formats, true) : startDate;
        let birthMoment =
            typeof birthDate === 'string' ? moment(birthDate, formats, true) : birthDate;

        if (birthMoment && !birthMoment.isValid() && (birthMoment as any)._i) {
            const originalInput = (birthMoment as any)._i;
            if (typeof originalInput === 'string') {
                birthMoment = moment(originalInput, formats, true);
            }
        }

        if (startMoment && !startMoment.isValid() && (startMoment as any)._i) {
            const originalInput = (startMoment as any)._i;
            if (typeof originalInput === 'string') {
                startMoment = moment(originalInput, formats, true);
            }
        }

        if (birthMoment && birthMoment.format('YYYY-MM-DD') === '1753-01-01') return 0;

        if (!birthMoment?.isValid() || !startMoment?.isValid()) {
            return 0;
        }

        const age = startMoment?.diff(birthMoment, 'days', true);

        return age && valueToUseInDivision !== 0 ? age / valueToUseInDivision : 0;
    };

    calculateAgeInDays = (birthDate: string): number => {
        const formats = [
            'YYYY-MM-DD',
            'DD/MM/YYYY',
            'DD/MM/YY',
            'D.M.YYYY',
            'DD.MM.YYYY',
            'D.M.YYYY HH.mm.ss',
            'DD.MM.YYYY HH.mm.ss',
            'D.M.YYYY H.mm.ss',
            'DD.MM.YYYY H.mm.ss',
        ];

        const birthMoment = moment(birthDate, formats, true);

        if (!birthMoment.isValid()) {
            return 0;
        }

        return moment().diff(birthMoment, 'days');
    };

    // Only supports if the EU identifier is in number format.
    formatEuIdentifier = (
        euNumber: string,
        euCheck: string | null = null,
        prefix: string = 'FI'
    ): string => {
        if (isNaN(Number(euNumber)) || !euNumber || euNumber?.length === 0) return euNumber;

        let eu: string = '',
            check: string = '';

        if (euNumber.length > 12) {
            eu = euNumber.substring(0, 12);
            check = euNumber.substring(12, 13);
        } else if (euNumber.length <= 12) {
            check = euCheck ?? euNumber.substring(euNumber.length - 1, euNumber.length);
            const zeros = '0'.repeat(12 - euNumber.length + 1);

            eu = zeros.concat(euNumber.substring(0, euNumber.length - 1));
        }

        return `${prefix}${eu}-${euCheck ?? check}`;
    };

    generateUniqueId(): string {
        const uniqueId = Math.round(new Date().valueOf() * Math.random());
        let id = `${this.getUiGeneratedString()}${uniqueId}`;
        if (this.uniqueIds.includes(id)) id = this.generateUniqueId();
        else this.uniqueIds.push(id);
        return id;
    }

    getFromLocalStorage<T>(key: string): T | null {
        const strValue = localStorage.getItem(this.storageKey(key));
        if (strValue === null) return null;
        return strValue !== 'undefined' ? JSON.parse(strValue) : undefined;
    }

    getFromSessionStorage<T>(key: string): T | null {
        const strValue = sessionStorage.getItem(this.storageKey(key));
        if (strValue === null) return null;
        return strValue !== 'undefined' ? JSON.parse(strValue) : undefined;
    }

    getUiGeneratedString(): string {
        return 'ui-generated-id-';
    }

    /** Returns a new array with unique items based on the specified key */
    getUniqueBy<T, K extends keyof T>(items: T[], key: K): T[] {
        return [...new Map(items.map((item) => [item[key], item])).values()];
    }

    isGuidEmpty(value: GUIDType | undefined | null): boolean {
        return (
            value == undefined || value === '00000000-0000-0000-0000-000000000000' || value === ''
        );
    }

    /**
     * Provide details for farm as parameter to check in case farm is inactivated.
     * @param farmDetails farm's detailed information
     */
    isFarmInactivated(farmDetails: IFarmDetails): boolean {
        return farmDetails.Inactivated ? true : false;
    }

    removeFromLocalStorage(key: string): void {
        localStorage.removeItem(this.storageKey(key));
    }

    removeFromSessionStorage(key: string): void {
        sessionStorage.removeItem(this.storageKey(key));
    }

    setToLocalStorage(key: string, value: any): void {
        const strValue = value !== undefined ? JSON.stringify(value) : 'undefined';
        localStorage.setItem(this.storageKey(key), strValue);
    }

    setToSessionStorage(key: string, value: any): void {
        const strValue = value !== undefined ? JSON.stringify(value) : 'undefined';
        sessionStorage.setItem(this.storageKey(key), strValue);
    }

    // This logic was taken from Mtech.Domain.Common library since we don't have a library for the same validations and whatnot to be used in web applications
    validateEuIdentifierCheckSum(identifier: string): boolean {
        if (isNotNil(identifier) && identifier.length === 16) {
            const num = identifier.substring(2, 14);
            const checksum = identifier.substring(15);
            let parsedNum: string;
            let parsedChecksum: number;

            // CheckNumber counts given parameter by modulus .....137137.
            const numbers = [1, 3, 7];

            if (!/^[0-9]+$/.test(num) || !/^[0-9]+$/.test(checksum)) {
                return false;
            }

            parsedNum = Number(num).toString().padStart(12, '0');
            parsedChecksum = parseInt(checksum, 10);

            // Count check code using all numbers
            const sum = parsedNum
                .split('')
                .map((ch, i) => parseInt(ch, 10) * numbers[i % 3])
                .reduce((acc, val) => acc + val, 0);

            // Take last mark of sum
            let check = sum % 10;
            check = 10 - check;

            const chk = check === 10 ? 0 : check;

            if (chk !== parsedChecksum) {
                return false;
            }

            return true;
        }

        return true;
    }

    private storageKey(key: string): string {
        return `${this.storageKeyPrefix}${key}`;
    }
}

export default new Utils();

export type { UiFormats, UtcString, LocalMoment, IFarmContext };
